const HTML_ELEMENT_PATTERN = /<\/?(?:p|br|strong|b|em|i|u|small|ul|ol|li|a|span|img|code|pre|blockquote)(?:\s|\/?>)/i;
const HASHTAG_PATTERN = /#([a-zA-Zа-яА-ЯёЁіІїЇєЄґҐ0-9_]+)/gu;

const escapeHtml = value => {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
};

export const normalizeEditorContent = value => {
    const content = String(value || '').replace(/\r\n?/g, '\n');

    if (!content.trim()) {
        return '';
    }

    if (HTML_ELEMENT_PATTERN.test(content)) {
        return content;
    }

    return content
        .split('\n')
        .map(line => line.length === 0 ? '<p><br></p>' : `<p>${escapeHtml(line)}</p>`)
        .join('');
};

const isSafeUrl = (value, allowDataImages = false) => {
    if (!value) return false;

    const url = value.trim();
    if (/^(https?:\/\/|\/)/i.test(url)) return true;

    return allowDataImages && /^data:image\/(?:gif|jpe?g|png|webp);base64,/i.test(url);
};

export const sanitizeRichTextFragment = (value, { allowImages = true } = {}) => {
    const source = document.createElement('div');
    source.innerHTML = normalizeEditorContent(value);

    source.querySelectorAll('script, style, iframe, object, embed').forEach(node => node.remove());

    const allowedTags = new Set([
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'small', 'ul', 'ol', 'li',
        'a', 'span', 'code', 'pre', 'blockquote',
        ...(allowImages ? ['img'] : []),
    ]);

    [...source.querySelectorAll('*')].forEach(node => {
        const tag = node.tagName.toLowerCase();

        if (!allowedTags.has(tag)) {
            node.replaceWith(...node.childNodes);
            return;
        }

        const attributes = Object.fromEntries(
            [...node.attributes].map(attribute => [attribute.name, attribute.value])
        );
        [...node.attributes].forEach(attribute => node.removeAttribute(attribute.name));

        if (tag === 'a') {
            const href = attributes.href;
            if (isSafeUrl(href)) {
                node.setAttribute('href', href);
                node.setAttribute('rel', 'noopener noreferrer');
            }
        } else if (tag === 'img' && allowImages) {
            const src = attributes.src;
            if (isSafeUrl(src, true)) {
                node.setAttribute('src', src);
                node.setAttribute('alt', attributes.alt || '');
            } else {
                node.remove();
            }
        } else if (tag === 'span') {
            const classes = (attributes.class || '').split(/\s+/);
            if (classes.includes('hashtag')) {
                node.setAttribute('class', 'hashtag text-indigo-600 dark:text-indigo-400 font-semibold');
                const tagName = attributes['data-tag'];
                if (tagName) node.setAttribute('data-tag', tagName);
            } else if (classes.includes('mention')) {
                node.setAttribute('class', 'mention');
                ['data-id', 'data-label', 'data-mention-suggestion-char'].forEach(attribute => {
                    const attributeValue = attributes[attribute];
                    if (attributeValue) node.setAttribute(attribute, attributeValue);
                });
            }
        }
    });

    return source.innerHTML;
};

export const structuredText = value => {
    const normalized = String(value || '').replace(/\r\n?/g, '\n');
    if (!normalized) return '';
    if (!HTML_ELEMENT_PATTERN.test(normalized)) return normalized;

    const container = document.createElement('div');
    container.innerHTML = normalized;

    const walk = node => {
        if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const tag = node.tagName.toLowerCase();
        if (tag === 'br') return '\n';

        const text = [...node.childNodes].map(walk).join('');
        if (['p', 'div', 'li', 'blockquote', 'pre'].includes(tag)) {
            const isEmptyParagraph = tag === 'p'
                && !node.textContent
                && [...node.childNodes].every(child =>
                    child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'br'
                );

            return isEmptyParagraph ? '\n' : `${text}\n`;
        }

        return text;
    };

    return [...container.childNodes]
        .map(walk)
        .join('')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/^\n+|\n+$/g, '');
};

export const textLength = value => structuredText(value).length;

const truncateText = (value, limit) => {
    if (limit === null || limit === undefined || value.length <= limit) return value;
    return `${value.slice(0, limit).trimEnd()}…`;
};

export const previewHtml = (value, limit = null, options = {}) => {
    const safeHtml = sanitizeRichTextFragment(value, options);
    if (limit === null || textLength(safeHtml) <= limit) return safeHtml;

    const container = document.createElement('div');
    container.innerHTML = safeHtml;
    const synchronizedHashtags = new Map();

    container.querySelectorAll('span.hashtag').forEach(hashtag => {
        const visibleName = (hashtag.textContent || '').trim().replace(/^#/, '');
        const canonicalName = hashtag.dataset.tag || visibleName;
        if (visibleName && canonicalName) {
            synchronizedHashtags.set(visibleName.toLocaleLowerCase('uk-UA'), canonicalName);
        }
    });

    const escaped = escapeHtml(truncateText(structuredText(safeHtml), limit)).replace(/\n/g, '<br>');

    return escaped.replace(HASHTAG_PATTERN, (token, name) => {
        const canonicalName = synchronizedHashtags.get(name.toLocaleLowerCase('uk-UA'));
        if (!canonicalName) return token;

        return `<span class="hashtag text-indigo-600 dark:text-indigo-400 font-semibold" data-tag="${escapeHtml(canonicalName)}">${token}</span>`;
    });
};
